import React from 'react'
import { SkeletonComponent } from '@syncfusion/ej2-react-notifications'
import type {
  TableSkeletonColumn,
  TableSkeletonProps,
} from './TableSkeleton.types'
import './TableSkeleton.css'

const DEFAULT_COLUMNS: TableSkeletonColumn[] = [
  { width: '14%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '16%', kind: 'text' },
  { width: '16%', kind: 'text' },
  { width: '10%', kind: 'text' },
  { width: '10%', kind: 'text' },
  { width: '12%', kind: 'badge' },
  { width: '10%', kind: 'actions' },
]

const isFixedWidth = (column: TableSkeletonColumn): boolean =>
  (column.kind ?? 'text') === 'spacer' || column.width.endsWith('px')

const columnStyle = (column: TableSkeletonColumn): React.CSSProperties =>
  isFixedWidth(column)
    ? { flex: `0 0 ${column.width}` }
    : { flex: `1 1 ${column.width}` }

const DEFAULT_TOOLBAR_WIDTHS = ['240px', '60px', '120px']

const renderCell = (
  column: TableSkeletonColumn,
  rowIndex: number,
  colIndex: number,
  actionCount: number,
): React.ReactElement => {
  const kind = column.kind ?? 'text'

  if (kind === 'spacer') {
    return <span className="ds-table-skeleton__spacer" />
  }

  if (kind === 'badge') {
    return (
      <SkeletonComponent
        shape="Rectangle"
        width="72px"
        height="18px"
        cssClass="ds-table-skeleton__badge"
        shimmerEffect="Fade"
      />
    )
  }

  if (kind === 'toggle') {
    return (
      <SkeletonComponent
        shape="Rectangle"
        width="32px"
        height="18px"
        cssClass="ds-table-skeleton__toggle"
        shimmerEffect="Fade"
      />
    )
  }

  if (kind === 'checkbox') {
    return (
      <SkeletonComponent
        shape="Rectangle"
        width="16px"
        height="16px"
        cssClass="ds-table-skeleton__checkbox"
        shimmerEffect="Fade"
      />
    )
  }

  if (kind === 'actions') {
    return (
      <div className="ds-table-skeleton__actions">
        {Array.from({ length: actionCount }).map((_, boxIndex) => (
          <SkeletonComponent
            key={`row-${rowIndex}-col-${colIndex}-action-${boxIndex}`}
            shape="Rectangle"
            width="20px"
            height="16px"
            shimmerEffect="Fade"
          />
        ))}
      </div>
    )
  }

  return (
    <SkeletonComponent
      shape="Rectangle"
      width="70%"
      height="14px"
      shimmerEffect="Fade"
    />
  )
}

const TableSkeleton: React.FC<TableSkeletonProps> = ({
  columns = DEFAULT_COLUMNS,
  rows = 8,
  showToolbar = false,
  showPagination = true,
  actionCount = 2,
  toolbarWidths = DEFAULT_TOOLBAR_WIDTHS,
  fillHeight = true,
  className = '',
}) => (
  <div
    className={`ds-table-skeleton${fillHeight ? '' : ' ds-table-skeleton--fit'}${
      className ? ` ${className}` : ''
    }`}
    aria-hidden="true"
  >
    <div className="ds-table-skeleton__table">
      {showToolbar ? (
        <div className="ds-table-skeleton__toolbar">
          {toolbarWidths.map((width, index) => (
            <SkeletonComponent
              key={`toolbar-${index}`}
              shape="Rectangle"
              width={width}
              height="32px"
              shimmerEffect="Fade"
            />
          ))}
        </div>
      ) : null}
      <div className="ds-table-skeleton__header">
        {columns.map((column, colIndex) => {
          const kind = column.kind ?? 'text'
          return (
            <div
              key={`header-${colIndex}`}
              className={`ds-table-skeleton__header-cell${
                kind === 'spacer'
                  ? ' ds-table-skeleton__header-cell--spacer'
                  : ''
              }`}
              style={columnStyle(column)}
            >
              {kind === 'spacer' ? null : (
                <SkeletonComponent
                  shape="Text"
                  width="50%"
                  height="16px"
                  shimmerEffect="Fade"
                />
              )}
            </div>
          )
        })}
      </div>
      <div className="ds-table-skeleton__body">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={`row-${rowIndex}`} className="ds-table-skeleton__row">
            {columns.map((column, colIndex) => {
              const kind = column.kind ?? 'text'
              return (
                <div
                  key={`row-${rowIndex}-col-${colIndex}`}
                  className={`ds-table-skeleton__cell${
                    kind === 'spacer' ? ' ds-table-skeleton__cell--spacer' : ''
                  }`}
                  style={columnStyle(column)}
                >
                  {renderCell(column, rowIndex, colIndex, actionCount)}
                </div>
              )
            })}
          </div>
        ))}
      </div>
      {showPagination ? (
        <div className="ds-table-skeleton__footer">
          <div className="ds-table-skeleton__pagination">
            {['30px', '30px', '30px', '30px'].map((width, index) => (
              <SkeletonComponent
                key={`pagination-${index}`}
                shape="Rectangle"
                width={width}
                height="16px"
                shimmerEffect="Fade"
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  </div>
)

export default TableSkeleton
